import { useEffect, useRef } from "react"
import { XIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"

export interface BillingRulesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultSection: "extra_credits" | "plans"
}

const BillingRulesDialog = ({
  open,
  onOpenChange,
  defaultSection,
}: BillingRulesDialogProps) => {
  const { t } = useTranslation()
  const extraCreditsRef = useRef<HTMLDivElement | null>(null)
  const plansRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const target =
      defaultSection === "extra_credits" ? extraCreditsRef : plansRef
    if (target.current) {
      target.current.scrollIntoView({ block: "start" })
    }
  }, [open, defaultSection])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[70vh] max-w-2xl overflow-y-auto"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <div className="flex items-start justify-between">
            <DialogTitle>{t("billing.plan.rules.title")}</DialogTitle>
            <DialogClose asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                aria-label={t("billing.plan.rules.closeRules")}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="text-base font-semibold">
              {t("billing.plan.rules.usageTitle")}
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>{t("billing.plan.rules.usageItem1")}</li>
              <li>{t("billing.plan.rules.usageItem2")}</li>
            </ul>
          </div>
          <div ref={extraCreditsRef} className="space-y-2">
            <div className="text-base font-semibold">
              {t("billing.plan.rules.extraCreditsTitle")}
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>{t("billing.plan.rules.extraCreditsItem1")}</li>
              <li>{t("billing.plan.rules.extraCreditsItem2")}</li>
              <li>{t("billing.plan.rules.extraCreditsItem3")}</li>
            </ul>
          </div>
          <div ref={plansRef} className="space-y-2">
            <div className="text-base font-semibold">
              {t("billing.plan.rules.plansTitle")}
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>{t("billing.plan.rules.plansItem1")}</li>
              <li>{t("billing.plan.rules.plansItem2")}</li>
              <li>{t("billing.plan.rules.plansItem2Example")}</li>
              <li>{t("billing.plan.rules.plansItem3")}</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="text-base font-semibold">
              {t("billing.plan.rules.alertsTitle")}
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>{t("billing.plan.rules.alertsItem1")}</li>
              <li>{t("billing.plan.rules.alertsItem2")}</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BillingRulesDialog
